/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ToastContainer } from "./components/ui/Toast";
import { Spinner } from "./components/ui/Spinner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { Login } from "./routes/Login";
import { PendingAccess } from "./routes/PendingAccess";
import { Shell } from "./routes/Shell";
import { Inbox } from "./routes/Inbox";
import { Runs } from "./routes/Runs";
import { SchedulingPool } from "./routes/SchedulingPool";
import { ManualRun } from "./routes/ManualRun";
import { StreamDetail } from "./routes/StreamDetail";
import { Placeholder } from "./routes/Placeholder";
import { Insights } from "./routes/Insights";
import { Forms } from "./routes/Forms";
import { Demo } from "./routes/Demo";
import { Guardrails } from "./routes/Guardrails";
import { Families } from "./routes/Families";
import { CareTeam } from "./routes/CareTeam";
import { Insurance } from "./routes/Insurance";
import { Duplicates } from "./routes/Duplicates";
import { DuplicateDetail } from "./routes/DuplicateDetail";
import { FamilyDetail } from "./routes/FamilyDetail";
import { Access } from "./routes/Access";
import { AccountDetail } from "./routes/AccountDetail";
import { ReferralOrganizations } from "./routes/ReferralOrganizations";
import { ReferralOrganizationDetail } from "./routes/ReferralOrganizationDetail";
import { ReferralProviders } from "./routes/ReferralProviders";
import { ReferralProviderDetail } from "./routes/ReferralProviderDetail";
import { ReferralDuplicates } from "./routes/ReferralDuplicates";
import { ReferralDuplicateDetail } from "./routes/ReferralDuplicateDetail";
import { ReferralFaxes } from "./routes/ReferralFaxes";
import { Referrals } from "./routes/Referrals";
import { ReferralDetail } from "./routes/ReferralDetail";
import { ReferralFaxDetail } from "./routes/ReferralFaxDetail";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();
  if (session.state === "loading") {
    return (
      <div className="grid h-screen place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (session.state === "anonymous") {
    return <Navigate to="/login" replace />;
  }
  if (session.user.roles.length === 0) {
    return <PendingAccess />;
  }
  return <>{children}</>;
}

function Toasts() {
  const { toasts, dismissToast } = useToast();
  return <ToastContainer toasts={toasts} onDismiss={dismissToast} />;
}

export function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                element={
                  <RequireAuth>
                    <Shell />
                  </RequireAuth>
                }
              >
                <Route path="/" element={<Navigate to="/inbox" replace />} />

                {/* Inbox — the unified work queue (live) */}
                <Route path="/inbox" element={<Inbox />} />
                <Route path="/inbox/claimed" element={<Inbox view="claimed" />} />
                <Route
                  path="/inbox/resolved"
                  element={
                    <Placeholder
                      title="Resolved"
                      description="Cases your team has closed out, with the note that settled them."
                      milestone="Q3, 2026"
                    />
                  }
                />

                {/* Agents — the autonomous workforce */}
                <Route path="/runs" element={<Navigate to="/agents/runs" replace />} />
                <Route path="/agents" element={<Navigate to="/agents/runs" replace />} />
                <Route path="/agents/runs" element={<Runs />} />
                <Route path="/streams/:id" element={<StreamDetail />} />
                <Route path="/agents/scheduling" element={<SchedulingPool />} />
                <Route path="/agents/manual-run" element={<ManualRun />} />

                {/* Care — the people */}
                <Route path="/care" element={<Navigate to="/care/families" replace />} />
                <Route path="/accounts" element={<Navigate to="/care/families" replace />} />
                <Route path="/accounts/:id" element={<AccountDetail />} />
                <Route path="/care/families" element={<Families />} />
                <Route path="/care/duplicates" element={<Duplicates />} />
                <Route path="/care/duplicates/:id" element={<DuplicateDetail />} />
                <Route path="/care/families/:id" element={<FamilyDetail />} />
                <Route path="/care/patients" element={<Navigate to="/care/families" replace />} />
                <Route
                  path="/care/patients/:id"
                  element={<Navigate to="/care/families" replace />}
                />
                <Route path="/care/providers" element={<Navigate to="/care/team" replace />} />
                <Route path="/care/team" element={<CareTeam />} />
                <Route path="/care/insurance" element={<Insurance />} />

                {/* Referrals — the external registry */}
                <Route path="/referrals" element={<Referrals />} />
                <Route path="/referrals/:id" element={<ReferralDetail />} />
                <Route path="/referrals/organizations" element={<ReferralOrganizations />} />
                <Route path="/referrals/organizations/:id" element={<ReferralOrganizationDetail />} />
                <Route path="/referrals/providers" element={<ReferralProviders />} />
                <Route path="/referrals/providers/:id" element={<ReferralProviderDetail />} />
                <Route path="/referrals/duplicates" element={<ReferralDuplicates />} />
                <Route path="/referrals/duplicates/:id" element={<ReferralDuplicateDetail />} />
                <Route path="/referrals/faxes" element={<ReferralFaxes />} />
                <Route path="/referrals/faxes/:id" element={<ReferralFaxDetail />} />

                {/* Intelligence — AI-native analytics */}
                <Route
                  path="/intelligence"
                  element={<Navigate to="/intelligence/insights" replace />}
                />
                <Route path="/intelligence/insights" element={<Insights />} />
                <Route
                  path="/intelligence/catalog"
                  element={
                    <Placeholder
                      title="Insight catalog"
                      description="Saved and pinned insights, shared across the team."
                      milestone="Q4, 2026"
                    />
                  }
                />
                <Route
                  path="/intelligence/reports"
                  element={
                    <Placeholder
                      title="Reports"
                      description="The recurring reports leadership watches, refreshed on a schedule."
                      milestone="Q4, 2026"
                    />
                  }
                />

                {/* Build — author the experiences */}
                <Route path="/build" element={<Navigate to="/build/forms" replace />} />
                <Route path="/build/forms" element={<Forms />} />
                <Route
                  path="/build/templates"
                  element={
                    <Placeholder
                      title="Message templates"
                      description="The SMS and email the agents send — edited here, versioned, and previewed."
                      milestone="Q4, 2026"
                    />
                  }
                />
                <Route
                  path="/build/content"
                  element={
                    <Placeholder
                      title="Content"
                      description="Daily tips and family questions surfaced across the family app."
                      milestone="Q4, 2026"
                    />
                  }
                />

                {/* Admin — governance the agents obey */}
                <Route path="/admin" element={<Navigate to="/admin/policies" replace />} />
                <Route path="/admin/guardrails" element={<Guardrails />} />
                <Route path="/admin/demo" element={<Demo />} />
                <Route
                  path="/admin/policies"
                  element={
                    <Placeholder
                      title="Policies"
                      description="Ranking weights, confidence thresholds, appointment bindings, and outreach timing — changes apply on the next agent restart."
                      milestone="Q4, 2026"
                    />
                  }
                />
                <Route path="/policy" element={<Navigate to="/admin/policies" replace />} />
                <Route path="/admin/payers" element={<Navigate to="/care/insurance" replace />} />
                <Route path="/payers" element={<Navigate to="/care/insurance" replace />} />
                <Route
                  path="/admin/sync"
                  element={
                    <Placeholder
                      title="Data sync"
                      description="Mirror health against Healthie, manual resync, and the webhook log."
                      milestone="Q4, 2026"
                    />
                  }
                />
                <Route path="/admin/access" element={<Access />} />

                <Route path="*" element={<Placeholder title="Not found" />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <Toasts />
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
